import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageRefresh } from '../../contexts/PageRefreshContext';
import { apiRequest } from '../../services/api';
import { fetchAcademicSessions, type AcademicSessionRecord } from '../../services/examApi';
import { getSchoolId, setSchoolId, clearSchoolId, setSchoolName, clearSchoolName, getAcademicSessionId, setAcademicSessionId, getUserName } from '../../services/auth';
import './TitleBar.css';
import '../layout/TopActionBar.css';

type SchoolOption = { id: string; name: string; school_code: string };

function LanguageSwitcher() {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const toggle = () => {
        i18n.changeLanguage(currentLang === 'hi' ? 'en' : 'hi');
    };

    return (
        <button className="lang-switcher" onClick={toggle} title="Switch language">
            <svg className="lang-switcher__icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span className="lang-switcher__label">{currentLang === 'hi' ? 'हिं' : 'EN'}</span>
        </button>
    );
}

function AcademicSessionSwitcher({ onChanged }: { onChanged?: () => void }) {
    const [sessions, setSessions] = useState<AcademicSessionRecord[]>([]);
    const [selectedId, setSelectedId] = useState<string>(getAcademicSessionId() ?? '');

    useEffect(() => {
        fetchAcademicSessions()
            .then((data) => {
                setSessions(data);
                // Use stored value if valid, otherwise default to is_current or first
                const stored = getAcademicSessionId();
                const valid = stored && data.some((s) => s.id === stored);
                if (!valid) {
                    const fallback = data.find((s) => s.is_current) ?? data[0];
                    if (fallback) { setSelectedId(fallback.id); setAcademicSessionId(fallback.id); }
                }
            })
            .catch(() => {});
    }, []);

    if (sessions.length === 0) return null;

    const handleChange = (id: string) => {
        setSelectedId(id);
        setAcademicSessionId(id);
        onChanged?.();
    };

    return (
        <select
            className="title-bar__session-select"
            value={selectedId}
            onChange={(e) => handleChange(e.target.value)}
        >
            {sessions.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
            ))}
        </select>
    );
}

function SchoolSwitcher({ onChanged }: { onChanged?: () => void }) {
    const { t } = useTranslation();
    const [schools, setSchools] = useState<SchoolOption[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(getSchoolId);
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        apiRequest<SchoolOption[]>('/api/schools?per_page=100')
            .then((res) => {
                setSchools(res.data);
                // Use stored value if valid, otherwise default to the first school
                const stored = getSchoolId();
                const current = stored ? res.data.find((s) => s.id === stored) : undefined;
                if (!current && res.data.length > 0) {
                    setSchoolId(res.data[0].id);
                    setSchoolName(res.data[0].name);
                    setSelectedId(res.data[0].id);
                } else if (current) {
                    // Keep the persisted school name in sync with the stored id
                    setSchoolName(current.name);
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selected = schools.find((s) => s.id === selectedId);

    const select = (id: string | null) => {
        if (id) {
            setSchoolId(id); setSelectedId(id);
            const school = schools.find((s) => s.id === id);
            if (school) setSchoolName(school.name);
        } else {
            clearSchoolId(); clearSchoolName(); setSelectedId(null);
        }
        setOpen(false);
        onChanged?.();
    };

    return (
        <div className="school-switcher" ref={ref}>
            <button className="school-switcher__trigger" onClick={() => setOpen((o) => !o)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M9 21V9h6v12" /><path d="M3 9h18" />
                </svg>
                <span className="school-switcher__label">{selected ? selected.name : t('common.allSchools')}</span>
                <svg className={`school-switcher__chevron${open ? ' school-switcher__chevron--open' : ''}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <div className="school-switcher__dropdown">
                    <button
                        className={`school-switcher__option${!selectedId ? ' school-switcher__option--active' : ''}`}
                        onClick={() => select(null)}
                    >
                        <span className="school-switcher__option-name">{t('common.allSchools')}</span>
                        {!selectedId && <svg className="school-switcher__check" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>}
                    </button>
                    {schools.length > 0 && <div className="school-switcher__divider" />}
                    {schools.map((s) => (
                        <button
                            key={s.id}
                            className={`school-switcher__option${s.id === selectedId ? ' school-switcher__option--active' : ''}`}
                            onClick={() => select(s.id)}
                        >
                            <span className="school-switcher__option-name">{s.name}</span>
                            <span className="school-switcher__option-code">{s.school_code}</span>
                            {s.id === selectedId && <svg className="school-switcher__check" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function getInitials(name: string | null): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function TitleBar() {
    const { t } = useTranslation();
    const refreshPage = usePageRefresh();
    const [isMac, setIsMac] = useState(true);
    const initials = getInitials(getUserName());

    useEffect(() => {
        window.electron?.getPlatform?.().then((platform) => {
            setIsMac(platform === 'darwin');
        });
    }, []);

    const handleMinimize = () => window.electron?.windowMinimize();
    const handleMaximize = () => window.electron?.windowMaximize();
    const handleClose = () => window.electron?.windowClose();

    return (
        <header className="title-bar">
            {/* macOS traffic lights need space on the left */}
            {isMac && <div className="title-bar__traffic-light-spacer" />}

            {/* Brand + Context Switcher */}
            <div className="title-bar__brand">
                <div className="title-bar__logo">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                    </svg>
                </div>
                <span className="title-bar__app-name">Gurukul</span>

                {/* Academic Session Selector */}
                <AcademicSessionSwitcher onChanged={refreshPage} />

                <SchoolSwitcher onChanged={refreshPage} />
            </div>

            {/* Global Search */}
            <div className="title-bar__search">
                <svg className="title-bar__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                    type="text"
                    placeholder={t('titleBar.searchPlaceholder')}
                    className="title-bar__search-input"
                />
            </div>

            {/* Actions (moved from TopActionBar) */}
            <div className="action-bar__actions">
                <LanguageSwitcher />

                <button className="action-bar__btn" aria-label={t('topBar.notifications')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                </button>

                <div className="action-bar__avatar">
                    <span>{initials}</span>
                </div>
            </div>

            {/* Window Controls (non-macOS only) */}
            {!isMac && (
                <div className="title-bar__controls">
                    <button className="title-bar__btn title-bar__btn--minimize" onClick={handleMinimize} aria-label={t('titleBar.minimize')}>
                        <svg width="12" height="12" viewBox="0 0 12 12"><rect y="5" width="12" height="1.5" rx="0.75" fill="currentColor" /></svg>
                    </button>
                    <button className="title-bar__btn title-bar__btn--maximize" onClick={handleMaximize} aria-label={t('titleBar.maximize')}>
                        <svg width="12" height="12" viewBox="0 0 12 12"><rect x="1" y="1" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg>
                    </button>
                    <button className="title-bar__btn title-bar__btn--close" onClick={handleClose} aria-label={t('titleBar.close')}>
                        <svg width="12" height="12" viewBox="0 0 12 12"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                    </button>
                </div>
            )}
        </header>
    );
}
